import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import ModalGroup from "../../Shared/Components/ModalGroup"
import Header from "../../Shared/Header/Header"
import Footer from "../../Shared/Layout/Footer"
import RoomHeader from "../Components/RoomHeader"
import RoomPost from "../RoomPost/RoomPost"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { setRoomData, setRoomModeratorData, setRoomOwnerData } from "../../Shared/SharedSlices/RoomSlice"
import { RootState } from "../../../App/store"
import { getRoomRequest } from "../RoomServiceRequests/RoomServiceRequests"
import { JoinStatus, RoomType } from "../../../App/enums"
import { getRoomPostsRequest } from "../RoomPost/RoomPostRequests"
import { setPostStatus, setRoomPostsData } from "../RoomPost/RoomPostSlice"
import { capitalizeFirstLetter } from "../../../App/Helpers/Helpers"
import { headers } from "../../../App/Axios"
import HTTPService from "../../../App/HTTPService"
import { setIsMember, setPendingType } from "../RoomSlices/MemberInRoomStatusSlice"
import BackgroundWrapper from "../../Shared/Components/BackgroundWrapper"
import RoomInformation from "./RoomInformation"
import { hasPostDropdown } from "../RoomPost/hasPostDropdown"
import { EventsTabIcon, LoadingAnimation, MediaTabIcon, MembersTabIcon, PostsCommentsTabIcon, PostsTabIcon } from "../../Shared/Components/SvgIcons"
import RoomMembers from "../Components/RoomMembers"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { HapticFeedback } from "../../../App/Helpers/HapticFeedback"
import { useScrollToTopOnTab } from "../../../App/Helpers/useScrollToTopOnTab"
import MediaLibrary from "./MediaLibrary"
import RoomEvents from "./RoomEvents"
import RoomPostTab from "./RoomPostTab/RoomPostTab";
import { PatchOrganizationTutorialProgressRequest } from "../../Organization/OrganizationSettings/OrganizationAccountServiceRequest"
import { setOrganizationData } from "../../Shared/SharedSlices/OrganizationsSlice"
import MobileBottomNav from "../../Shared/Layout/MobileBottomNav";


function OrganizationRoomScreen() {
    const translation = useSelector(selectTranslations)
    const { roomId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get('tab') || 'posts'
    const searchParamsString = searchParams.toString()

    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadingPost, setLoadingPost] = useState(false)
    const scrollContainer = useRef<HTMLDivElement>(null)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const dispatch = useDispatch()
    const isRoomOwner = useSelector((state: RootState) => state.roomSlice.roomOwnerSlice.room_owner)
    const allow_requests = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.allow_requests!
    const invite_others = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.invite_others!
    const share_documents = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.share_documents!
    const only_admins_can_upload = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.only_admins_can_upload!
    const posts = useSelector((state: RootState) => state.RoomPostsSlice.roomPostsSlice.posts)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const canSeePosts = roomDataState.type === RoomType.Private ? isMemberInRoom === JoinStatus.MEMBER || isRoomOwner : true
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const navigate = useNavigate()
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const postStatus = useSelector((state: RootState) => state.RoomPostsSlice.postStatusSlice.postsStatus)
    const tabIndex =
        tabParam === 'members'
            ? 1
            : tabParam === 'new-post'
                ? 2
                : tabParam === 'media'
                    ? 3
                    : tabParam === 'events'
                        ? 4
                        : 0

    // Scroll-to-top on tab tap
    const { handleTabSelect: handleScrollToTop, registerScrollContainer } = useScrollToTopOnTab(tabIndex)

    useEffect(() => {
        const syncOnboardingStep = async () => {
            const shouldStartAtStepTwo = searchParams.get("start_org_step") === "2"

            if (shouldStartAtStepTwo && organizationDataState.id && (organizationDataState.tutorial_progress || 0) < 2) {
                await PatchOrganizationTutorialProgressRequest(organizationDataState.id, 2)
                dispatch(setOrganizationData({ ...organizationDataState, tutorial_progress: 2 }))
            }
        }

        syncOnboardingStep()
    }, [dispatch, organizationDataState, searchParams])

    useEffect(() => {
        const updated = new URLSearchParams(searchParamsString)
        switch (tabParam) {
            case 'posts':
                updated.delete('file')
                updated.delete('event')
                break
            case 'members':
                updated.delete('file')
                updated.delete('event')
                break
            case 'new-post':
                updated.delete('file')
                break
            case 'media':
                updated.delete('event')
                break
            case 'events':
                updated.delete('file')
                break
            default:
                updated.set('tab', 'posts')
                updated.delete('file')
                updated.delete('event')
        }

        if (updated.toString() !== searchParamsString) {
            setSearchParams(updated, { replace: true })
        }
    }, [tabParam, searchParamsString, setSearchParams])

    const updateSearchParamsForTab = (index: number) => {
        const nextTab =
            index === 1
                ? 'members'
                : index === 2
                    ? 'new-post'
                    : index === 3
                        ? 'media'
                        : index === 4
                            ? 'events'
                            : 'posts'

        const updated = new URLSearchParams(searchParamsString)
        updated.set('tab', nextTab)

        if (nextTab === 'posts' || nextTab === 'members') {
            updated.delete('file')
            updated.delete('event')
        } else if (nextTab === 'new-post') {
            updated.delete('file')
        } else if (nextTab === 'media') {
            updated.delete('event')
        } else if (nextTab === 'events') {
            updated.delete('file')
        }

        if (updated.toString() !== searchParamsString) {
            setSearchParams(updated, { replace: true })
        }
    }

    const onFABClick = () => {
        HapticFeedback.medium()
        updateSearchParamsForTab(2)
    }

    useEffect(() => {
        let isMounted = true
        getRoomRequest(parseInt(roomId!))
            .then(response => {
                if (isMounted && response.status === 200) {
                    dispatch(setRoomData(response.data))
                }
            })
            .catch(error => {
                if (error.response.status === 404) {
                    navigate("/not-found")
                }
            })
    }, [
        dispatch,
        roomId,
        roomDataState.name,
        roomDataState.photo,
        roomDataState.type,
        allow_requests,
        only_admins_can_upload,
        invite_others,
        share_documents,
        roomDataState.description,
        navigate,
    ])

    const getUserStatus = useCallback(() => {
        return HTTPService.get(`/user-status-in-room/${roomId}/?org=${organizationDataState.id}`, headers)
    }, [organizationDataState.id, roomId])

    useEffect(() => {
        if (roomDataState.id && roomDataState.id === parseInt(roomId!)) {
            getUserStatus().then(response => {
                if (response.status === 200) {
                    dispatch(setIsMember(response.data.org_status.status))
                    dispatch(setPendingType({ type: response.data.org_status.type, notification_id: response.data.org_status.notification_id }))
                }
            })
        }
    }, [dispatch, getUserStatus, roomDataState.id, roomId])

    useEffect(() => {
        dispatch(setRoomOwnerData(roomDataState?.organization?.id === organizationDataState.id))
    }, [dispatch, organizationDataState.id, roomDataState?.organization?.id, roomId])

    useEffect(() => {
        dispatch(setPostStatus({ postsStatus: true, singularPostStatus: false, postedInRoomStatus: false }))
    }, [dispatch])

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller
        if (postStatus) {
            getRoomPostsRequest(parseInt(roomId!), 1)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomPostsData([...posts, ...response.data.results]))
                        setHasNextPage(Boolean(response.data.next))
                        setLoadingPost(false)
                        dispatch(setPostStatus({ postsStatus: false, singularPostStatus: false, postedInRoomStatus: false }))
                    }
                })
                .catch(error => {
                    if (signal.aborted) return
                    console.log(error)
                })

            return () => {
                setPageNum(1)
                controller.abort()
            }
        }
    }, [dispatch, roomId, postStatus])

    const onScroll = () => {
        if (scrollContainer.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current

            if (Math.ceil(scrollTop + clientHeight + 1) >= scrollHeight && hasNextPage && !postStatus && !loadingPost) {
                const controller = new AbortController()
                const { signal } = controller
                setLoadingPost(true)

                getRoomPostsRequest(parseInt(roomId!), pageNum + 1)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setRoomPostsData([...posts, ...response.data.results]))
                            setHasNextPage(Boolean(response.data.next))
                            setLoadingPost(false)
                            setPageNum(pageNum + 1)
                        }
                    })
                    .catch(error => {
                        if (signal.aborted) return
                        console.log(error)
                    })
                return () => controller.abort()
            }
        }
    }

    useEffect(() => {
        let filtered = posts.filter(post => post.room === parseInt(roomId!))
        dispatch(setRoomPostsData(filtered))
    }, [roomId])

    useEffect(() => {
        dispatch(setRoomModeratorData(roomDataState?.room_moderators?.some(moderator => moderator.id === organizationDataState.id!)!))
    }, [dispatch, roomDataState?.room_moderators, roomId, organizationDataState.id])

    return (
        <BackgroundWrapper>
            <Header roomHeader={true} />
            <div className="room-screen">
                <div className="room-layout">
                    <RoomInformation isMemberInRoom={isMemberInRoom} hasPrivelegedPermission={isRoomOwner} />
                    <div className="room-layout__right-panel">
                        <div
                            className={`room-layout__right-panel-header ${(isMemberInRoom === JoinStatus.NON_MEMBER || JoinStatus.PENDING) && "room-layout__right-panel-header--join-button"
                                }`}
                        >
                            <RoomHeader
                                roomType={capitalizeFirstLetter(roomDataState?.type! === RoomType.Private ? translation.private : translation.public)}
                            />
                        </div>

                        <Tabs className="tabs-container" selectedIndex={tabIndex} onSelect={(index) => {
                            HapticFeedback.selectionChanged();
                            handleScrollToTop(index);
                            updateSearchParamsForTab(index);
                        }}>
                            <TabPanel>
                                <div 
                                    className="room-layout__right-panel-content" 
                                    ref={(el) => {
                                        if (scrollContainer.current !== el) {
                                            scrollContainer.current = el;
                                        }
                                        registerScrollContainer(0, el);
                                    }} 
                                    onScroll={onScroll}
                                >
                                    {roomDataState.type && !canSeePosts ? (
                                        <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
                                    ) : posts!.length > 0 ? (
                                        posts?.map((post, index) => {
                                            return (
                                                <RoomPost
                                                    key={post.id}
                                                    showCommentIcon={true}
                                                    postDropdown={hasPostDropdown(post, role, organizationDataState.id!, userDataState.id, isRoomOwner)}
                                                    postDropdownStyle={
                                                        index === posts.length - 1
                                                            ? "room-post__right-panel-content room-post__right-panel-content--no-height"
                                                            : "room-post__right-panel-content room-post__right-panel-content--height-5"
                                                    }
                                                    noCursor={post.content_object === null ? true : false}
                                                    post={post}
                                                    postType={"posts"}
                                                    showFullPost={false}
                                                />
                                            )
                                        })
                                    ) : (
                                        <div className="room-layout__no-post">{translation.nopostsYet}</div>
                                    )}

                                    {loadingPost && <LoadingAnimation type="post" />}
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="room-layout__right-panel-content room-layout__right-panel-content--member-panel">
                                    <RoomMembers />
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <RoomPostTab />
                            </TabPanel>
                            <TabPanel className={'media-library__tabs-panel'}>
                                <MediaLibrary />
                            </TabPanel>
                            <TabPanel className={'room-events__tabs-panel'}>
                                <RoomEvents />
                            </TabPanel>
                            {tabParam !== 'new-post' && (
                                <button type="button" className="mobile-bottom-nav__fab" onClick={onFABClick} aria-label="Post erstellen">
                                    <PostsCommentsTabIcon />
                                </button>
                            )}
                            <MobileBottomNav isRoomMenu={true} />
                            <TabList>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <PostsTabIcon />
                                        <span>Austausch</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <MembersTabIcon />
                                        <span>Mitglieder</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <PostsCommentsTabIcon />
                                        <span>Posten</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <MediaTabIcon />
                                        <span>Mediathek</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <EventsTabIcon />
                                        <span>Events</span>
                                    </div>
                                </Tab>
                            </TabList>
                        </Tabs>

                    </div>
                </div>
            </div>
            <Footer roomfooter={true} />
            <ModalGroup />
        </BackgroundWrapper>
    )
}

export default OrganizationRoomScreen
